import React from 'react';
import TeachEnglishPageLayout from '../components/TeachEnglishPageLayout';

const QuizStudents = () => {
  const title = 'Quiz Your Students';
  const image = '/Images/quiz1.webp';
  const intro = 'Low-stakes quizzes are among the highest-impact teaching tools: they boost retention, reveal gaps, and motivate without anxiety. This guide covers quiz design principles, question types, and ready-to-use templates for every level.';

  const seoContent = [
    'The testing effect (retrieval practice) is robust: recalling information strengthens memory more than re-studying. A 5-minute weekly quiz outperforms a 30-minute review session. Key principles: keep stakes low (formative, not graded), mix recent and older material (spacing), vary question formats, and give immediate feedback with explanations.',
    'Match question type to learning goal. Multiple choice: efficient for breadth, good for diagnosis—write plausible distractors targeting common errors. Gap-fill: tests productive knowledge, especially collocations and grammar forms. Short answer: reveals depth, allows partial credit. True/False: quick but 50% guess rate—add \"justify your answer.\" Matching: great for vocab sets and function-exponent pairs.',
    'Design quizzes using a specification grid: rows = topics/skills, columns = cognitive levels (recognize, recall, apply, analyze). Aim for 60% lower-order, 40% higher-order. This ensures coverage and prevents \"quiz = only vocab definitions.\"',
    'Digital tools automate the cycle: Google Forms / Microsoft Forms (auto-grading, branching, response charts), Kahoot/Quizizz (gamified, live or homework), Moodle/Canvas quiz (question banks, adaptive). Export results to a tracker spreadsheet for trend-spotting: which items discriminate? Which students plateau?',
    'Close the loop. After the quiz: 1) Show answer key with explanations. 2) Students self-mark and note \"why I missed this.\" 3) Teacher analyzes patterns—re-teach the top 2–3 misconceptions next lesson. 4) Students set a micro-goal: \"I will review past perfect before Thursday.\" The quiz becomes a learning event, not a judgment.',
  ];

  const levels = [
    {
      level: 'design',
      label: 'Quiz Design Resources',
      tagline: 'Templates & specification grids',
      cards: [
        {
          title: 'Quiz Spec Grid Template',
          image: '/Images/grammar1.webp',
          desc: 'Excel/Sheets template: map topics × cognitive levels → balanced quiz blueprint.',
          links: [{ text: 'Copy Template', to: '/tools/quiz-spec-grid' }],
        },
        {
          title: 'Question Writing Checklist',
          image: '/Images/2people1.webp',
          desc: '12-point quality control: stem clarity, distractor plausibility, one correct answer, etc.',
          links: [{ text: 'Download', to: '/resources/question-checklist.pdf' }],
        },
        {
          title: 'CEFR-Aligned Item Banks',
          image: '/Images/vocabulary.webp',
          desc: 'Searchable banks by level, grammar point, vocab set, and skill—export to Forms/Kahoot.',
          links: [{ text: 'Browse Banks', to: '/quizzes/item-banks' }],
        },
      ],
    },
    {
      level: 'tools',
      label: 'Digital Quiz Platforms',
      tagline: 'Setup guides & class decks',
      cards: [
        {
          title: 'Google Forms Masterclass',
          image: '/Images/online-teacher.webp',
          desc: 'Sections, branching, auto-feedback, response validation, grade import to Sheets.',
          links: [{ text: 'Watch Guide', to: '/videos/google-forms-quiz' }],
        },
        {
          title: 'Kahoot! for Formative Assessment',
          image: '/Images/tutor2-1280x470.webp',
          desc: 'Live vs. challenge mode, question types, reports, sharing with co-teachers.',
          links: [{ text: 'Watch Guide', to: '/videos/kahoot-formative' }],
        },
        {
          title: 'Quizizz Homework Mode',
          image: '/Images/3people1a.webp',
          desc: 'Student-paced, memes, power-ups, LMS integration, mastery dashboard.',
          links: [{ text: 'Watch Guide', to: '/videos/quizizz-homework' }],
        },
      ],
    },
    {
      level: 'ready',
      label: 'Ready-Made Quizzes',
      tagline: 'Import and assign',
      cards: [
        {
          title: 'Grammar Diagnostic A1–B2',
          image: '/Images/quiz1.webp',
          desc: '30-item adaptive placement quiz with CEFR mapping and auto-feedback.',
          links: [{ text: 'Take Quiz', to: '/quizzes/grammar-diagnostic' }],
        },
        {
          title: 'Vocabulary Progress Checks',
          image: '/Images/quiz1.webp',
          desc: 'Unit-aligned 10-item quizzes for 20 common coursebook topics (A2–B1).',
          links: [{ text: 'Browse Quizzes', to: '/quizzes/vocab-progress' }],
        },
      ],
    },
  ];

  return (
    <TeachEnglishPageLayout
      title={title}
      image={image}
      intro={intro}
      seoContent={seoContent}
      levels={levels}
      seoTitle="Quiz Your Students: Formative Assessment Design, Tools & Ready-Made Banks"
      seoDescription="Guide to low-stakes quizzing for ESL. Retrieval practice principles, question-type taxonomy, specification grids, Google Forms/Kahoot/Quizizz setup, and free CEFR-aligned item banks."
    />
  );
};

export default QuizStudents;